// src/pages/admin/AdminMedia.tsx
import React, { useEffect, useState } from 'react';
import { collection, doc, getDocs, getDoc, updateDoc, orderBy, query } from 'firebase/firestore';
import { ref as sRef, getDownloadURL } from 'firebase/storage';
import { db, storage, uploadFileWithToken } from '../../lib/firebase';
import { clearBookCache } from '../../services/exerciseService';
import { useThemePalette } from '../../hooks/useThemePalette';
import { useThemeStore } from '../../stores/useThemeStore';

interface BookEntry { id: string; title: string; units: number; }

const inputStyle = (C: ReturnType<typeof useThemePalette>, isDark: boolean): React.CSSProperties => ({
  padding: '9px 12px', borderRadius: '10px',
  border: `1px solid ${C.border}`,
  background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
  color: C.text, fontSize: '0.85rem', fontFamily: 'inherit', outline: 'none',
});

const btnStyle = (color: string): React.CSSProperties => ({
  padding: '8px 20px', borderRadius: '999px', border: 'none',
  background: color, color: '#fff', fontSize: '0.78rem',
  fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
});

export default function AdminMedia() {
  const C      = useThemePalette();
  const isDark = useThemeStore(s => s.theme) === 'dark';

  const [books,       setBooks]       = useState<BookEntry[]>([]);
  const [bookId,      setBookId]      = useState('');
  const [unitNum,     setUnitNum]     = useState('1');
  const [unitCount,   setUnitCount]   = useState(1);
  const [audioUrl,    setAudioUrl]    = useState('');
  const [videoUrl,    setVideoUrl]    = useState('');
  const [audioFile,   setAudioFile]   = useState<File | null>(null);
  const [status,      setStatus]      = useState('');
  const [isErr,       setIsErr]       = useState(false);

  useEffect(() => {
    getDocs(query(collection(db, 'books'), orderBy('displayOrder')))
      .then(snap => {
        const list: BookEntry[] = [];
        snap.forEach(d => list.push({ id: d.id, title: d.data().title as string, units: d.data().units as number ?? 0 }));
        setBooks(list);
      })
      .catch(() => {});
  }, []);

  // Load existing media when book+unit changes
  useEffect(() => {
    if (!bookId) return;
    const book = books.find(b => b.id === bookId);
    setUnitCount(book?.units ?? 1);
    getDoc(doc(db, 'books', bookId)).then(snap => {
      if (!snap.exists()) return;
      const data = snap.data();
      const media = (data.unitMedia ?? {})[`unit${unitNum}`] as { audioUrl?: string; videoUrl?: string } | undefined;
      setAudioUrl(media?.audioUrl ?? '');
      setVideoUrl(media?.videoUrl ?? '');
    }).catch(() => {});
  }, [bookId, unitNum, books]);

  const save = async () => {
    if (!bookId) { setStatus('Select a book first'); setIsErr(true); return; }
    setStatus('Saving...'); setIsErr(false);
    try {
      let finalAudioUrl = audioUrl;

      if (audioFile) {
        const path = `media/${bookId}/unit${unitNum}/audio_${Date.now()}_${audioFile.name}`;
        const storageRef = sRef(storage, path);
        await uploadFileWithToken(storageRef, audioFile);
        finalAudioUrl = await getDownloadURL(storageRef);
      }

      await updateDoc(doc(db, 'books', bookId), {
        [`unitMedia.unit${unitNum}`]: {
          audioUrl: finalAudioUrl || null,
          videoUrl: videoUrl || null,
        },
      });
      clearBookCache();
      setAudioUrl(finalAudioUrl);
      setAudioFile(null);
      setStatus('Saved!'); setIsErr(false);
      setTimeout(() => setStatus(''), 3000);
    } catch (e: unknown) {
      setStatus(e instanceof Error ? e.message : 'Error saving');
      setIsErr(true);
    }
  };

  const unitOptions = Array.from({ length: unitCount }, (_, i) => i + 1);

  return (
    <div style={{ padding: '32px', maxWidth: '640px' }}>
      <div style={{ fontSize: '1.1rem', fontWeight: 800, color: C.text, marginBottom: '8px' }}>Media Manager</div>
      <div style={{ fontSize: '0.8rem', color: C.muted, marginBottom: '28px' }}>Upload audio and set video URL per unit</div>

      {/* Selection */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', flexWrap: 'wrap' }}>
        <select
          value={bookId}
          onChange={e => { setBookId(e.target.value); setUnitNum('1'); setAudioUrl(''); setVideoUrl(''); setAudioFile(null); }}
          style={{ ...inputStyle(C, isDark), flex: '1 1 180px' }}
        >
          <option value="">— Select Book —</option>
          {books.map(b => <option key={b.id} value={b.id}>{b.title}</option>)}
        </select>

        <select
          value={unitNum}
          onChange={e => setUnitNum(e.target.value)}
          disabled={!bookId}
          style={{ ...inputStyle(C, isDark), flex: '0 0 110px' }}
        >
          {unitOptions.map(n => <option key={n} value={String(n)}>Unit {n}</option>)}
        </select>
      </div>

      {bookId && (
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: '16px', padding: '24px' }}>
          {/* Audio */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ fontSize: '0.82rem', fontWeight: 700, color: C.text, display: 'block', marginBottom: '8px' }}>
              Audio File (MP3)
            </label>
            {audioUrl && (
              <div style={{ fontSize: '0.75rem', color: C.success, marginBottom: '8px', wordBreak: 'break-all' }}>
                Current: {audioUrl.split('/').pop()?.split('?')[0] ?? audioUrl}
              </div>
            )}
            <input
              type="file"
              accept="audio/mp3,audio/mpeg,audio/*"
              onChange={e => setAudioFile(e.target.files?.[0] ?? null)}
              style={{ fontSize: '0.82rem', color: C.text }}
            />
            {audioFile && (
              <div style={{ fontSize: '0.75rem', color: C.premium, marginTop: '6px' }}>
                Selected: {audioFile.name}
              </div>
            )}
          </div>

          {/* Video URL */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{ fontSize: '0.82rem', fontWeight: 700, color: C.text, display: 'block', marginBottom: '8px' }}>
              Video URL (YouTube or direct)
            </label>
            <input
              value={videoUrl}
              onChange={e => setVideoUrl(e.target.value)}
              placeholder="https://..."
              style={{ ...inputStyle(C, isDark), width: '100%', boxSizing: 'border-box' }}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <button onClick={() => void save()} style={btnStyle(C.accent)}>Save Media</button>
            {status && <span style={{ fontSize: '0.78rem', color: isErr ? C.error : C.success }}>{status}</span>}
          </div>
        </div>
      )}
    </div>
  );
}
